import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMutation } from '@apollo/client';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Container,
  Paper,
  Snackbar,
  Alert
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { RESET_PASSWORD_MUTATION } from '../graphql/mutations';

const ChangePassword = () => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState(new Array(6).fill(''));
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [loading, setLoading] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info', // 'error' | 'success' | 'warning' | 'info'
  });

  const inputsRef = useRef([]);
  const [ChangePassword] = useMutation(RESET_PASSWORD_MUTATION);
  const [hasSentOtp, setHasSentOtp] = useState(false);

  const phoneNumber = localStorage.getItem('mobile');
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  useEffect(() => {
    if (inputsRef.current[0]) setTimeout(() => inputsRef.current[0].focus(), 100);
  }, []);

  useEffect(() => {
    if (!hasSentOtp || isOtpVerified) return;

    if (timer > 0) {
      const countdown = setTimeout(() => setTimer((prev) => prev - 1), 1000);
      return () => clearTimeout(countdown);
    } else {
      setCanResend(true);
    }
  }, [timer, isOtpVerified, hasSentOtp]);

  const showMessage = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const sendOtp = async () => {
    setLoading(true);
    try {
      const mobile = localStorage.getItem('mobile');
      const response = await fetch('https://gcp-poc-with-apollo-1081525845464.asia-south1.run.app/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile }),
      });

      const data = await response.json();

      if (data.status === 'success') {
        showMessage('OTP sent successfully!', 'success');
        setTimer(300);
        setCanResend(false);
        setHasSentOtp(true);
      } else {
        showMessage(data.message || 'Failed to send OTP', 'error');
      }
    } catch (error) {
      showMessage('Network error. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, '');
    const newOtp = [...otp];

    if (value.length === 6) {
      value.split('').forEach((digit, i) => (newOtp[i] = digit));
      setOtp(newOtp);
      inputsRef.current[5]?.focus();
      return;
    }

    newOtp[index] = value[0] || '';
    setOtp(newOtp);
    if (value && index < 5) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const verifyOtp = async () => {
    window.scrollTo(0, 0);
    if (!otp.every((d) => d)) {
      return showMessage('Please enter the complete OTP.', 'error');
    }

    setLoading(true);
    try {
      const response = await verifyOtpApi(otp.join(''));
      if (response.success) {
        showMessage('OTP verified successfully!', 'success');
        setTimeout(() => {
          setIsOtpVerified(true);
        }, 800);
      } else {
        showMessage(response.message || 'OTP verification failed.', 'error');
      }
    } catch (err) {
      showMessage('Server error. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = () => {
    setOtp(new Array(6).fill(''));
    setTimer(300);
    setCanResend(false);
    sendOtp();
    setTimeout(() => inputsRef.current[0]?.focus(), 100);
  };

  const validatePassword = (password) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$%^&*!])[A-Za-z\d@#$%^&*!]{8,}$/;
    return regex.test(password) && !/\s/.test(password);
  };

  const handleChangePassword = async () => {
    window.scrollTo(0, 0);

    // Step 1: Check if both fields are filled
    if (!newPassword || !confirmPassword) {
      return showMessage("Please fill in all required fields.", "error");
    }

    // Step 2: Validate password format
    if (!validatePassword(newPassword)) {
      return showMessage(
        "Password must be at least 8 characters long, include uppercase, lowercase, a number, and a special character (@#$%^&*!). No spaces allowed.",
        "error"
      );
    }

    // Step 3: Check if passwords match
    if (newPassword !== confirmPassword) {
      return showMessage("Passwords do not match.", "error");
    }

    // Step 4: Proceed to API call
    setLoading(true);
    try {
      const response = await changePasswordApi(newPassword);
      if (response.success) {
        showMessage("Password changed successfully!", "success");
        setTimeout(() => {
          navigate("/login");
        }, 1200);
      } else {
        showMessage(response.message || "Password change failed.", "error");
      }
    } catch (err) {
      showMessage("Server error. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtpApi = async (otp) => {
    try {
      const mobile = localStorage.getItem('mobile');
      const response = await fetch('https://gcp-poc-with-apollo-1081525845464.asia-south1.run.app/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile, otp }),
      });

      const data = await response.json();

      if (data.status === 'error') {
        return { success: false, message: data.message || 'OTP verification failed.' };
      }

      return { success: true };
    } catch (error) {
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  const changePasswordApi = async (password) => {
    const id = localStorage.getItem('id');

    try {
      const { data } = await ChangePassword({
        variables: {
          Id: id,
          New_Password: password,
        },
      });

      return { success: true, message: data.changePassword };
    } catch (error) {
      const message =
        error?.graphQLErrors?.[0]?.message ||
        error?.message ||
        'Network error. Please try again.';

      return { success: false, message };
    }
  };

  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: "#f8f9e9",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Container maxWidth="xs">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Paper
            elevation={4}
            sx={{
              p: 4,
              borderRadius: 4,
              backgroundColor: "#f8f9e9",
            }}
          >
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <LockResetIcon sx={{ fontSize: 40, color: "#2A289C" }} />
              <Typography variant="h5" fontWeight={600} mt={1}>
                Change Password
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Secure your account with a new password
              </Typography>
            </Box>

            <motion.div
              key={isOtpVerified ? "password" : "otp"}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {!isOtpVerified ? (
                <>
                  <TextField
                    fullWidth
                    label="Mobile Number"
                    value={`+91 ${phoneNumber}`}
                    InputProps={{ readOnly: true }}
                    size="small"
                    sx={{
                      mb: 3,
                      '& .MuiInputBase-root': { height: 40, fontSize: "14px" },
                      '& .MuiInputLabel-root': { fontSize: "14px" },
                    }}
                  />

                  {!hasSentOtp ? (
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={sendOtp}
                      disabled={loading}
                      sx={{ mb: 2, borderRadius: "999px", backgroundColor: "#2A289C" }}
                    >
                      {loading ? "Sending..." : "Send OTP"}
                    </Button>
                  ) : (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 2,
                        }}
                      >
                        {otp.map((digit, index) => (
                          <TextField
                            key={index}
                            type="tel"
                            inputMode="numeric"
                            value={digit}
                            onChange={(e) => handleOtpChange(e, index)}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                            inputRef={(el) => (inputsRef.current[index] = el)}
                            variant="outlined"
                            inputProps={{
                              maxLength: 1,
                              style: {
                                textAlign: "center",
                                fontSize: "20px",
                                padding: 10,
                              },
                            }}
                            sx={{ width: 45 }}
                          />
                        ))}
                      </Box>

                      <Button
                        fullWidth
                        variant="contained"
                        onClick={verifyOtp}
                        disabled={loading}
                        sx={{
                          backgroundColor: "#2A289C",
                          borderRadius: "999px",
                          mb: 1,
                        }}
                      >
                        {loading ? "Verifying..." : "Verify OTP"}
                      </Button>

                      <Button
                        fullWidth
                        onClick={resendOtp}
                        disabled={!canResend}
                        sx={{
                          color: canResend ? "#2A289C" : "text.disabled",
                          borderRadius: "999px",
                          border: "1px solid",
                          borderColor: canResend ? "#2A289C" : "transparent",
                          mt: 1,
                        }}
                        variant="outlined"
                      >
                        {canResend ? 'Resend OTP' : `Resend in ${formatTime(timer)}`}
                      </Button>
                    </>
                  )}

                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      navigate("/");
                      window.scrollTo(0, 0);
                    }}
                    sx={{
                      borderRadius: "999px",
                      color: "#2A289C",
                      borderColor: "#2A289C",
                      mt: 2,
                    }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    label="New Password"
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    sx={{ mb: 3 }}
                    required
                    InputProps={{
                      endAdornment: (
                        <IconButton onClick={() => setShowNewPassword(!showNewPassword)}>
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Confirm Password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    sx={{ mb: 3 }}
                    required
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleChangePassword}
                    disabled={loading}
                    sx={{
                      backgroundColor: "#2A289C",
                      borderRadius: "999px",
                    }}
                  >
                    {loading ? "Saving..." : "Change Password"}
                  </Button>
                </>
              )}
            </motion.div>
          </Paper>
        </motion.div>
      </Container>

      {/* Snackbar for messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChangePassword;
